import React, { useRef, useState } from 'react';
import { t } from '../../locales/translate';
import { useAsyncIndicator } from '../../util/util';
import { Button, ButtonProps } from '../Button/Button';
import { Modal, Props } from './Modal';

interface ControlledModalConfiguration {
  canSubmit?: boolean;
  submitting?: boolean;
  onSubmit?: () => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
  saveButtonProps?: ButtonProps;
  actions?: () => React.ReactNode;
  extraFooter?: () => React.ReactNode;
}

export interface ModalController<T> {
  close: () => void;
  model: T;
  configure: (config: ControlledModalConfiguration) => void;
}

/*
  DEPRECATED: Use `Modal` instead.

  Example:
      <Modal open={displayModal} title={t.actions.edit(t.model.news._name)}>
        <NewsForm news={newsItem} onClose={() => setDisplayModal(false)} />
      </Modal>
*/
export function ControlledModal<T = undefined>(
  props: Props & {
    onClose: () => void;
    model?: T;
    children: (controller: ModalController<T>) => React.ReactNode;
  }
): JSX.Element {
  const { open, children, model, onClose, saveTitleOverride } = props;
  const shouldRenderRef = useRef(!!open);
  const teardownTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const persistantModelRef = useRef(model);
  const [isExecuting, executeAsync] = useAsyncIndicator();
  const configRef = useRef<ControlledModalConfiguration>({});
  const hasCalledConfigureRef = useRef(false);
  const [, forceUpdate] = useState(0);

  const controller: ModalController<T> = {
    model: model || persistantModelRef.current || ({} as T),
    close: onClose,
    configure: (config: ControlledModalConfiguration) => {
      configRef.current = config;
      // this is preventing an infinite rerender loop. If configure is called the first time, rerender the content. On second try, skip the update (this is the rerender already).
      if (hasCalledConfigureRef.current) {
        hasCalledConfigureRef.current = false;
      } else {
        hasCalledConfigureRef.current = true;
        forceUpdate((val) => val + 1);
      }
    },
  };
  const render = !!open || shouldRenderRef.current;

  shouldRenderRef.current = render;

  if (model) {
    persistantModelRef.current = model;
  }

  if (open && teardownTimerRef.current) {
    // cancel dismissal
    clearTimeout(teardownTimerRef.current);
  }

  if (!open && shouldRenderRef.current && !teardownTimerRef.current) {
    // time to tear this modal content down
    teardownTimerRef.current = setTimeout(() => {
      shouldRenderRef.current = false;
      persistantModelRef.current = undefined;
    }, 600);
  }

  const content = render ? children(controller) : null;

  const actions = props.actions || render ? configRef.current.actions && configRef.current.actions() : null;

  const { onCancel, onSubmit } = configRef.current;
  const closing = !open && shouldRenderRef.current;
  let { submitting, canSubmit } = configRef.current;

  if (isExecuting || closing) {
    submitting = true;
  }
  if (submitting || closing) {
    canSubmit = false;
  }

  const modalProps = {
    ...props,
    actions,
    footer: (
      <>
        <Button
          title={t.actions.cancel('')}
          onClick={() => {
            if (onCancel) {
              onCancel();
            }
            onClose();
          }}
        />
        <Button
          title={saveTitleOverride || t.actions.save('')}
          type="primary"
          loading={submitting}
          disabled={!canSubmit}
          onClick={() => {
            if (onSubmit) {
              const returnType = onSubmit();
              if (returnType) {
                executeAsync(returnType);
              }
            }
          }}
          {...configRef.current.saveButtonProps}
        />
        {configRef.current.extraFooter && configRef.current.extraFooter()}
      </>
    ),
  };

  return <Modal {...modalProps}>{content}</Modal>;
}
